import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { CheckCircle, XCircle, Mail, Loader } from 'lucide-react';
import { API_BASE_URL } from '../../config/api.js';
import { applyLanguageFromCountry } from '../../utils/languageFromCountry';
import { getJobsLandingPath } from '../../utils/routingUtils';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [hasToken, setHasToken] = useState(false);

  useEffect(() => {
    const verifyEmail = async () => {
      let token = searchParams.get('token');
      
      // Try to decode token if it's URL encoded
      if (token) {
        try {
          token = decodeURIComponent(token);
        } catch (e) {
          // If decoding fails, use original token
          console.warn("Token decoding failed, using original:", e);
        }
      }
      
      console.log("🔍 Email verification - Token from URL:", token ? token.substring(0, 20) + "..." : "MISSING");
      console.log("  Full token length:", token?.length || 0);
      
      if (!token) {
        setStatus('error');
        setMessage('Verifikacioni token nije pronađen.');
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/company/verify-email`, {
          params: { token }
        });

        if (response.data) {
          setStatus('success');
          setMessage(response.data.message || 'Email je uspešno verifikovan!');
          setEmail(response.data.email || '');
          setCompanyName(response.data.companyName || '');
          
          // If token is provided, store it and login automatically
          if (response.data.token && response.data.result) {
            setHasToken(true);
            console.log("✅ Auto-login after email verification");
            const userData = {
              result: response.data.result,
              token: response.data.token
            };
            
            localStorage.setItem("profile", JSON.stringify(userData));
            localStorage.setItem("userType", "company");
            
            // Store company slug if available
            if (response.data.result?.slug) {
              localStorage.setItem('currentCompanySlug', response.data.result.slug);
            }
            
            // Store business type
            if (response.data.result?.businessType) {
              sessionStorage.setItem('businessType', response.data.result.businessType);
            }

            // Jezik: Srbija → srpski, ostalo → engleski
            applyLanguageFromCountry(response.data.result?.countryCode);
            
            // Redirect to dashboard after 2 seconds
            setTimeout(() => {
              const companySlug = response.data.result?.slug;
              navigate(getJobsLandingPath(companySlug), { replace: true });
            }, 2000);
          } else {
            setHasToken(false);
            // Redirect to login after 3 seconds if no token
            setTimeout(() => {
              navigate('/auth?role=company&type=login');
            }, 3000);
          }
        }
      } catch (error) {
        setStatus('error');
        const errorMessage = error.response?.data?.message || 
          'Greška prilikom verifikacije emaila. Token možda nije važeći ili je istekao.';
        setMessage(errorMessage);
        
        // Try to extract email from error or keep existing
        if (error.response?.data?.email) {
          setEmail(error.response.data.email);
        }
        
        console.error("Email verification error:", {
          message: errorMessage,
          token: token ? token.substring(0, 10) + "..." : "MISSING",
          status: error.response?.status,
          data: error.response?.data
        });
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const handleResend = async () => {
    const emailToResend = email || prompt('Unesite vašu email adresu:');
    
    if (!emailToResend) {
      toast.error('Email adresa je obavezna');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/company/resend-verification`, {
        email: emailToResend
      });
      
      toast.success(response.data?.message || 'Verifikacioni email je ponovo poslat. Proverite vašu poštu.');
      
      // Update email state if we got it from prompt
      if (!email && emailToResend) {
        setEmail(emailToResend);
      }
    } catch (error) {
      console.error("Resend verification error:", error);
      toast.error(
        error.response?.data?.message || 
        'Greška prilikom slanja verifikacionog emaila.'
      );
    }
  };

  const handleGoToLogin = () => {
    navigate('/auth?role=company&type=login');
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-8">
        <div className="text-center">
          {status === 'verifying' && (
            <>
              <div className="flex justify-center mb-6">
                <Loader className="h-16 w-16 text-blue-500 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">
                Verifikacija emaila...
              </h2>
              <p className="text-gray-300">
                Molimo sačekajte dok verifikujemo vašu email adresu.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="bg-green-500 rounded-full p-4">
                  <CheckCircle className="h-12 w-12 text-white" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">
                Email uspešno verifikovan!
              </h2>
              <p className="text-gray-300 mb-6">
                {message}
              </p>
              <p className="text-sm text-gray-400 mb-6">
                {hasToken ? 'Preusmeravamo vas u aplikaciju...' : 'Preusmeravamo vas na stranicu za prijavu...'}
              </p>
              {!hasToken && (
                <button
                  onClick={handleGoToLogin}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition-colors"
                >
                  Idi na prijavu
                </button>
              )}
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="bg-red-500 rounded-full p-4">
                  <XCircle className="h-12 w-12 text-white" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">
                Greška pri verifikaciji
              </h2>
              <p className="text-gray-300 mb-6">
                {message}
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleResend}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
                >
                  <Mail className="h-5 w-5 mr-2" />
                  Pošalji ponovo verifikacioni email
                </button>
                <button
                  onClick={handleGoToLogin}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-lg transition-colors"
                >
                  Idi na prijavu
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;

